import React, { useState } from 'react';
import { X, Loader, CheckCircle, AlertCircle } from 'lucide-react';

const AddClientModal = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [testSuccess, setTestSuccess] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    website: '',
    storeUrl: '',
    consumerKey: '',
    consumerSecret: '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
    setTestSuccess(false);

    if (field === 'name') {
      const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').trim();
      setFormData(prev => ({ ...prev, slug }));
    }

    if (field === 'storeUrl' && !formData.website) {
      setFormData(prev => ({ ...prev, website: value }));
    }
  };

  const testConnection = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${window.location.hostname === "localhost" ? "http://localhost:3000" : "https://syncbridge.co.za/api"}/woocommerce/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeUrl: formData.storeUrl,
          consumerKey: formData.consumerKey,
          consumerSecret: formData.consumerSecret,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setTestSuccess(true);
      } else {
        setError('Connection failed. Check your credentials.');
      }
    } catch (err) {
      setError('Connection failed. Check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const tenantRes = await fetch(`${window.location.hostname === "localhost" ? "http://localhost:3000" : "https://syncbridge.co.za/api"}/tenants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          slug: formData.slug,
          website: formData.website,
        }),
      });

      if (!tenantRes.ok) {
        const err = await tenantRes.json();
        throw new Error(err.message || 'Failed to create client');
      }

      const tenant = await tenantRes.json();

      const wooRes = await fetch(`${window.location.hostname === "localhost" ? "http://localhost:3000" : "https://syncbridge.co.za/api"}/woocommerce/connections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: tenant.id,
          storeUrl: formData.storeUrl,
          consumerKey: formData.consumerKey,
          consumerSecret: formData.consumerSecret,
        }),
      });

      if (!wooRes.ok) {
        throw new Error('Failed to connect WooCommerce');
      }

      onSuccess();
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const canGoStep2 = formData.name && formData.slug;
  const canSubmit = testSuccess;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>{step === 1 ? 'Client Details' : 'WooCommerce Connection'}</h2>
          <button style={styles.closeBtn} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div style={styles.body}>
          {step === 1 && (
            <div>
              <div style={styles.field}>
                <label style={styles.label}>Client Name</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="e.g. Delicate Couches"
                  value={formData.name}
                  onChange={e => handleChange('name', e.target.value)}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Slug</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="delicate-couches"
                  value={formData.slug}
                  onChange={e => handleChange('slug', e.target.value)}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Website (optional)</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="https://example.com"
                  value={formData.website}
                  onChange={e => handleChange('website', e.target.value)}
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <div style={styles.field}>
                <label style={styles.label}>Store URL</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="https://yourstore.com"
                  value={formData.storeUrl}
                  onChange={e => handleChange('storeUrl', e.target.value)}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Consumer Key</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="ck_..."
                  value={formData.consumerKey}
                  onChange={e => handleChange('consumerKey', e.target.value)}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Consumer Secret</label>
                <input
                  type="password"
                  style={styles.input}
                  placeholder="cs_..."
                  value={formData.consumerSecret}
                  onChange={e => handleChange('consumerSecret', e.target.value)}
                />
              </div>

              <button
                style={{
                  ...styles.testBtn,
                  backgroundColor: testSuccess ? '#f0fdf4' : '#f3f4f6',
                  color: testSuccess ? '#16a34a' : '#374151',
                }}
                onClick={testConnection}
                disabled={loading || !formData.storeUrl || !formData.consumerKey || !formData.consumerSecret}
              >
                {loading ? (
                  <Loader size={16} className="spin" />
                ) : testSuccess ? (
                  <CheckCircle size={16} />
                ) : null}
                {testSuccess ? 'Connected' : 'Test Connection'}
              </button>
            </div>
          )}

          {error && (
            <div style={styles.error}>
              <AlertCircle size={16} />
              {error}
            </div>
          )}
        </div>

        <div style={styles.footer}>
          {step === 2 && (
            <button style={styles.backBtn} onClick={() => setStep(1)}>
              Back
            </button>
          )}
          <div style={{ flex: 1 }} />
          {step === 1 ? (
            <button
              style={{ ...styles.primaryBtn, opacity: canGoStep2 ? 1 : 0.5 }}
              onClick={() => setStep(2)}
              disabled={!canGoStep2}
            >
              Next
            </button>
          ) : (
            <button
              style={{ ...styles.primaryBtn, opacity: canSubmit ? 1 : 0.5 }}
              onClick={handleSubmit}
              disabled={!canSubmit || loading}
            >
              {loading ? 'Creating...' : 'Create Client'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '440px',
    maxHeight: '90vh',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb',
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#6b7280',
  },
  body: {
    padding: '24px',
  },
  field: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  testBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '10px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    marginTop: '8px',
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    fontSize: '14px',
    marginTop: '16px',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 24px',
    borderTop: '1px solid #e5e7eb',
    gap: '12px',
  },
  backBtn: {
    padding: '10px 16px',
    backgroundColor: '#f3f4f6',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    cursor: 'pointer',
  },
  primaryBtn: {
    padding: '10px 20px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
};

export default AddClientModal;

